import { ArrowDownRight, ArrowUpRight, ThumbsDown, ThumbsUp, X } from 'lucide-react'
import { toast } from 'sonner'

import { useAnalyticsSignals, useUpdateAnalyticsSignal } from '@/hooks/features/analytics'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'

import type { AnalyticsSignal } from '@/types/analytics'

const METRIC_LABELS: Record<AnalyticsSignal['metric'], string> = {
  conversion_rate: 'Conversion rate',
  add_to_cart_rate: 'Add-to-cart rate',
  checkout_completion_rate: 'Checkout completion',
  sessions: 'Sessions',
  product_add_to_cart_rate: 'Add-to-cart rate',
  product_views: 'Visits viewing',
  page_lcp: 'Loading time (LCP)',
  error_sessions: 'Visits hitting an error',
}

const PAGE_LABELS: Record<string, string> = { home: 'home page', product: 'product pages', category: 'category pages', checkout: 'checkout', cart: 'cart' }
const CHANNEL_LABELS: Record<string, string> = { direct: 'direct visits', search: 'search', social: 'social', paid: 'paid ads', email: 'email', referral: 'other sites' }

const isRate = (metric: AnalyticsSignal['metric']) => metric.endsWith('_rate')

const value = (s: AnalyticsSignal, v: number) =>
  isRate(s.metric) ? `${(v * 100).toFixed(1)}%` : s.metric === 'page_lcp' ? `${(v / 1000).toFixed(1)} s` : `${Math.round(v).toLocaleString()} a week`

const segment = (s: AnalyticsSignal) => {
  const parts: string[] = []
  if (s.subject?.type === 'product') parts.push(`for ${s.subject.name ?? 'a deleted product'}`)
  if (s.subject?.type === 'page') parts.push(`on ${PAGE_LABELS[s.subject.id] ?? `${s.subject.id} pages`}`)
  if (s.dimension.key === 'device') parts.push(`on ${s.dimension.value}`)
  if (s.dimension.key === 'channel') parts.push(`from ${CHANNEL_LABELS[s.dimension.value] ?? s.dimension.value}`)
  return parts.join(' ')
}

/** "Conversion rate on mobile fell 42%: 3.1% → 1.8%". Facts only; why is left to the merchant. */
const headline = (s: AnalyticsSignal) =>
  `${METRIC_LABELS[s.metric]} ${segment(s)} ${s.change_pct > 0 ? 'rose' : 'fell'} ${Math.abs(Math.round(s.change_pct))}%: ${value(s, s.baseline)} → ${value(s, s.current)}`
    .replace(/\s+/g, ' ')

const SAMPLE_UNIT: Partial<Record<AnalyticsSignal['metric'], string>> = {
  checkout_completion_rate: 'checkouts',
  product_add_to_cart_rate: 'product views',
  product_views: 'product views',
  page_lcp: 'page loads measured',
  error_sessions: 'visits',
}

const evidence = (s: AnalyticsSignal) => {
  const unit = SAMPLE_UNIT[s.metric] ?? 'sessions'
  const confidence = s.p_value === null ? '' : s.p_value < 0.001 ? ' · very unlikely to be chance' : ` · ${Math.round((1 - s.p_value) * 100)}% unlikely to be chance`
  return `${s.window.current_from} – ${s.window.current_to} vs the 28 days before · ${s.n_current.toLocaleString()} vs ${s.n_baseline.toLocaleString()} ${unit}${confidence}`
}

const SEVERITY: Record<AnalyticsSignal['severity'], { label: string; tone: 'error' | 'warning' | 'success' | 'soft' }> = {
  high: { label: 'Needs attention', tone: 'error' },
  medium: { label: 'Worth a look', tone: 'warning' },
  low: { label: 'For your information', tone: 'soft' },
}

/**
 * Changes worth a look, found every night by comparing the last week with the four before it. Each one shows the
 * numbers behind it; merchants can mark it useful or not (which tunes future signals) or dismiss it.
 */
export const SignalsCard = () => {
  const signals = useAnalyticsSignals()
  const update = useUpdateAnalyticsSignal()
  const act = (id: string, body: { status?: AnalyticsSignal['status']; feedback?: AnalyticsSignal['feedback'] }) =>
    update.mutate({ id, ...body }, { onError: (e) => toast.error(e.message) })

  const d = signals.data
  const waiting = d && d.history_days < d.min_history_days

  return (
    <Card>
      <CardHeader>
        <CardTitle className="w-fit text-sm font-semibold underline decoration-faint decoration-dotted underline-offset-4">Changes worth a look</CardTitle>
      </CardHeader>
      <CardContent>
        {signals.isLoading ? (
          <Skeleton className="h-20" />
        ) : signals.isError ? (
          <p className="text-sm text-error">{signals.error.message}</p>
        ) : waiting ? (
          <p className="text-sm text-muted-foreground">
            Every night your store's last week is compared with the four weeks before it, and real changes show up here. This needs {d.min_history_days} days of
            visits; your store has {d.history_days} so far.
          </p>
        ) : !d?.result.length ? (
          <p className="text-sm text-muted-foreground">Nothing changed much in the last week compared with the four weeks before.</p>
        ) : (
          <ul className="divide-y divide-border-subtle">
            {d.result.map((s) => {
              const Arrow = s.change_pct > 0 ? ArrowUpRight : ArrowDownRight
              return (
                <li key={s._id} className="flex flex-col gap-2 py-3 first:pt-0 last:pb-0 sm:flex-row sm:items-start sm:justify-between">
                  <div className="flex min-w-0 gap-3">
                    <span
                      className={`grid size-8 shrink-0 place-items-center rounded-lg ${s.good ? 'bg-success-lighter text-success-dark' : 'bg-error-lighter text-error-dark'}`}
                      aria-hidden="true"
                    >
                      <Arrow className="size-4" />
                    </span>
                    <div className="min-w-0 space-y-1">
                      <p className="text-sm font-medium text-ink">
                        {headline(s)}
                        <Badge tone={s.good ? 'success' : SEVERITY[s.severity].tone} className="ml-2 align-middle">
                          {s.good ? 'Good news' : SEVERITY[s.severity].label}
                        </Badge>
                      </p>
                      <p className="text-xs text-muted-foreground">{evidence(s)}</p>
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-1 pl-11 sm:pl-0">
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label="Useful"
                      aria-pressed={s.feedback === 'useful'}
                      className={s.feedback === 'useful' ? 'bg-fill text-ink' : ''}
                      onClick={() => act(s._id, { feedback: s.feedback === 'useful' ? null : 'useful' })}
                    >
                      <ThumbsUp />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label="Not useful"
                      aria-pressed={s.feedback === 'not_useful'}
                      className={s.feedback === 'not_useful' ? 'bg-fill text-ink' : ''}
                      onClick={() => act(s._id, { feedback: s.feedback === 'not_useful' ? null : 'not_useful' })}
                    >
                      <ThumbsDown />
                    </Button>
                    <Button variant="ghost" size="icon-sm" aria-label="Dismiss" onClick={() => act(s._id, { status: 'dismissed' })}>
                      <X />
                    </Button>
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  )
}
